import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Collapse,
    Badge,
} from '@mui/material';
import {
    Update as UpdateIcon,
    School as CourseIcon,
    Article as LessonIcon,
    CheckCircle as ApplyIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify, usePermissions } from 'react-admin';
import { LibraryReference, libraryService } from '../../services/libraryService';

interface UpdateNotificationsProps {
    showCard?: boolean;
    onUpdatesChange?: (count: number) => void;
}

const UpdateNotifications: React.FC<UpdateNotificationsProps> = ({
    showCard = true,
    onUpdatesChange,
}) => {
    const [updates, setUpdates] = useState<LibraryReference[]>([]);
    const [loading, setLoading] = useState(true);
    const [applying, setApplying] = useState<number | null>(null);
    const [expanded, setExpanded] = useState(true);

    const translate = useTranslate();
    const notify = useNotify();
    const { permissions, isLoading: permissionsLoading } = usePermissions();

    const hasPermission = !permissionsLoading && Array.isArray(permissions) &&
        (permissions.includes('ROLE_CONTENT_MANAGER') ||
         permissions.includes('ROLE_COMPANY_ADMIN') ||
         permissions.includes('ROLE_OPERATOR'));

    useEffect(() => {
        if (hasPermission) {
            loadUpdates();
        }
    }, [hasPermission]);

    useEffect(() => {
        onUpdatesChange?.(updates.length);
    }, [updates.length, onUpdatesChange]);

    const loadUpdates = async () => {
        setLoading(true);
        try {
            const data = await libraryService.checkForUpdates();
            setUpdates(data);
        } catch (error) {
            console.error('Failed to check for updates:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyUpdate = async (reference: LibraryReference) => {
        setApplying(reference.id);
        try {
            await libraryService.applyUpdate(reference.id);
            notify(translate('library.updates.applySuccess'), { type: 'success' });
            loadUpdates();
        } catch (error: any) {
            notify(error.response?.data?.error || translate('library.updates.applyError'), { type: 'error' });
        } finally {
            setApplying(null);
        }
    };

    if (!hasPermission || permissionsLoading) {
        return null;
    }

    if (loading) {
        if (!showCard) return null;
        return (
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>{translate('library.updates.checking')}</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (updates.length === 0) {
        if (!showCard) return null;
        return null; // Don't show anything if no updates
    }

    const content = (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Badge badgeContent={updates.length} color="primary">
                        <UpdateIcon color="primary" />
                    </Badge>
                    <Typography variant="h6">
                        {translate('library.updates.title')}
                    </Typography>
                </Box>
                <IconButton size="small">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                    {translate('library.updates.description', { count: updates.length })}
                </Alert>

                <List dense>
                    {updates.map((update) => (
                        <ListItem key={update.id} sx={{ py: 1 }}>
                            <ListItemIcon>
                                {update.targetType === 'course' ? (
                                    <CourseIcon color="primary" />
                                ) : (
                                    <LessonIcon color="secondary" />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary={update.libraryItem?.title || `${update.targetType} #${update.targetId}`}
                                secondary={
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                        <Chip
                                            size="small"
                                            label={update.targetType === 'course'
                                                ? translate('library.types.course')
                                                : translate('library.types.lesson')}
                                        />
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={translate('library.updates.versionChange', {
                                                from: update.sourceVersionAtCreation,
                                                to: update.currentVersion,
                                            })}
                                        />
                                    </Box>
                                }
                            />
                            <ListItemSecondaryAction>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={applying === update.id ? <CircularProgress size={16} /> : <ApplyIcon />}
                                    onClick={() => handleApplyUpdate(update)}
                                    disabled={applying !== null}
                                >
                                    {translate('library.updates.apply')}
                                </Button>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </Collapse>
        </>
    );

    if (!showCard) {
        return <Box>{content}</Box>;
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>{content}</CardContent>
        </Card>
    );
};

export default UpdateNotifications;
